'use client'

import type { ReactNode, Ref } from 'react'
import {
  Combobox,
  ComboboxList,
  ComboboxProvider,
  type ComboboxProps,
  type ComboboxProviderProps,
} from '@ariakit/react'
import { SearchIcon } from 'lucide-react'
import * as styles from './Menu.css'

/** Marks a row the search must not auto-select; see `searchContext`. */
export const SEARCH_CONTEXT_ATTRIBUTE = 'data-search-context'

const firstSelectableRow: ComboboxProps['getAutoSelectId'] = (items) =>
  items.find(
    (item) =>
      !item.disabled && !item.element?.hasAttribute(SEARCH_CONTEXT_ATTRIBUTE),
  )?.id

/**
 * Wrap a `MenuProvider` in this to give its menu a search field. The query
 * resets whenever the menu closes.
 */
export function MenuSearchProvider({
  value,
  onValueChange,
  children,
}: {
  value: string
  onValueChange: (value: string) => void
  children: ComboboxProviderProps['children']
}) {
  return (
    <ComboboxProvider value={value} setValue={onValueChange} resetValueOnHide>
      {children}
    </ComboboxProvider>
  )
}

/** The search field at the top of a menu inside {@link MenuSearchProvider}. */
export function MenuSearchField({
  placeholder,
  ref,
}: {
  placeholder: string
  ref?: Ref<HTMLInputElement>
}) {
  return (
    <div className={styles.search}>
      <SearchIcon size={15} aria-hidden />
      <Combobox
        ref={ref}
        autoSelect
        getAutoSelectId={firstSelectableRow}
        placeholder={placeholder}
        aria-label={placeholder}
        className={styles.searchInput}
        // In a side submenu Ariakit closes the menu on ArrowLeft without
        // checking for a text field; keep both arrows for the caret.
        onKeyDownCapture={(event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.stopPropagation()
          }
        }}
      />
    </div>
  )
}

/** The filtered rows under {@link MenuSearchField}. */
export function MenuSearchList({ children }: { children: ReactNode }) {
  return <ComboboxList>{children}</ComboboxList>
}

/** Stands in for the rows when a search matches nothing. */
export function MenuEmpty({ children }: { children: ReactNode }) {
  return <div className={styles.empty}>{children}</div>
}
